'use client';

import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';

const stats = [
  { value: '10,000+', label: 'Practice Questions', icon: '📚' },
  { value: '19', label: 'Legal Topics Covered', icon: '⚖️' },
  { value: '95%', label: 'Pass Rate Among Users', icon: '🏆' },
  { value: '5,000+', label: 'Students Enrolled', icon: '👨‍⚖️' },
];

const features = [
  {
    title: 'Full-Length Mock Tests',
    description: 'Simulate the real AIBE exam with timed, full-length tests covering all subjects exactly as they appear on exam day.',
    icon: '📝',
    size: 'large',
  },
  {
    title: 'Topic-Based Practice',
    description: 'Drill down into specific legal topics — Constitutional Law, CPC, IPC, Evidence Act, and more.',
    icon: '🎯',
    size: 'small',
  },
  {
    title: 'Detailed Analytics',
    description: 'Track your weak areas, accuracy trends, and time per question with a comprehensive performance dashboard.',
    icon: '📊',
    size: 'small',
  },
  {
    title: 'Instant Answer Review',
    description: 'Every question comes with detailed explanations so you understand the law, not just the answer.',
    icon: '💡',
    size: 'medium',
  },
  {
    title: 'Achievements & Streaks',
    description: 'Stay motivated with daily streaks, badges, and milestones as you progress toward exam readiness.',
    icon: '🏅',
    size: 'medium',
  },
];

const faqs = [
  {
    question: 'What is AIBE and who needs to take it?',
    answer:
      'AIBE (All India Bar Examination) is a mandatory exam conducted by the Bar Council of India for law graduates who wish to practice as advocates in India. Every person enrolled as an advocate under the Advocates Act, 1961 must clear AIBE to obtain a Certificate of Practice.',
  },
  {
    question: 'How does Crack AIBE help me prepare for the exam?',
    answer:
      'Crack AIBE offers 10,000+ practice questions mapped to every AIBE topic, full-length timed mock tests that simulate the real exam, topic-based practice sessions, instant answer explanations, and a detailed analytics dashboard that shows your weak areas and progress over time.',
  },
  {
    question: 'What topics are covered on the platform?',
    answer:
      'The platform covers all 19 AIBE topics including Constitutional Law, Indian Penal Code/BNS, Code of Civil Procedure, Evidence Act/BSA, Criminal Procedure Code/BNSS, Family Law, Company Law, Labour & Industrial Law, Environmental Law, Intellectual Property, Taxation, Cyber Law, ADR, Administrative Law, Land Acquisition, Specific Relief/Contract/Property/NI Act, Tort/Consumer/MV Act, Professional Ethics, and PIL.',
  },
  {
    question: 'What subscription plans are available?',
    answer:
      'We currently offer a 6-Month Access plan that gives you full, unlimited access to all practice questions, mock tests, analytics, and platform features for 6 months from the date of purchase. After 6 months, your subscription expires and access is revoked until renewed.',
  },
  {
    question: 'Can I use my account on multiple devices at the same time?',
    answer:
      'No. Each Crack AIBE account is restricted to a single active session at a time. If you log in on a new device or browser, your previous session will be automatically terminated. This policy is in place to prevent account sharing and ensure fair access for all subscribers. You can still use your account across different devices — just not simultaneously.',
  },
  {
    question: 'What happens when my 6-month subscription expires?',
    answer:
      'Once your 6-month access period ends, your account will automatically be set to expired status and you will no longer be able to access practice questions, mock tests, or analytics. Your account and progress data are retained. You can renew your subscription at any time to regain full access.',
  },
  {
    question: 'How is Crack AIBE different from other AIBE prep resources?',
    answer:
      'Unlike generic quiz apps or static PDFs, Crack AIBE is built exclusively for AIBE preparation. Every question is mapped to the official AIBE syllabus, answers come with detailed legal explanations, and the analytics engine pinpoints exactly which topics need more attention — saving you time and helping you study smarter.',
  },
  {
    question: 'Can I review my answers after a mock test?',
    answer:
      'Yes. After completing any mock test or topic-based practice session, you can review every question with the correct answer and a detailed explanation. The review interface also shows your time spent per question and highlights patterns in your mistakes.',
  },
  {
    question: 'How do I get support if I face an issue?',
    answer:
      'You can reach our support team at info@crackaibe.com. We typically respond within 24 hours on business days. For account or payment-related issues, please include your registered email address in your message.',
  },
];

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden transition-all duration-200 hover:border-indigo-300">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left bg-white hover:bg-slate-50 transition-colors duration-200"
        aria-expanded={open}
      >
        <span className="text-sm sm:text-base font-semibold text-slate-800 leading-snug">{question}</span>
        <span
          className={`flex-shrink-0 w-6 h-6 rounded-full border border-slate-300 flex items-center justify-center transition-transform duration-300 ${open ? 'rotate-45 border-indigo-400 bg-indigo-50' : ''}`}
        >
          <svg className="w-3 h-3 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
          </svg>
        </span>
      </button>
      {open && (
        <div className="px-6 pb-5 pt-1 bg-slate-50">
          <p className="text-sm text-slate-600 leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
}

function AnimatedCounter({ target, suffix = '' }: { target: number; suffix?: string }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const duration = 1800;
          const steps = 60;
          const increment = target / steps;
          let current = 0;
          const timer = setInterval(() => {
            current += increment;
            if (current >= target) {
              setCount(target);
              clearInterval(timer);
            } else {
              setCount(Math.floor(current));
            }
          }, duration / steps);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target]);

  return (
    <span ref={ref}>
      {count.toLocaleString()}
      {suffix}
    </span>
  );
}

export default function HomePage() {
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [topics] = useState<string[]>([
    'ADR',
    'Administrative Law',
    'Code of Civil Procedure',
    'Company Law',
    'Constitutional Law',
    'Criminal Procedure Code/BNSS',
    'Cyber Law',
    'Environmental Law',
    'Evidence Act/BSA',
    'Family Law',
    'Indian Penal Code/BNS',
    'Intellectual Property',
    'Labour & Industrial Law',
    'Land Acquisition',
    'Specific relief/Contract/Property/NI Act',
    'Taxation',
    'Tort/Consumer/MV Act',
    'Professional Ethics',
    'PIL',
  ]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePos({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 overflow-x-hidden">
      {/* Cursor glow */}
      <div
        className="pointer-events-none fixed inset-0 z-0 transition-opacity duration-300"
        style={{
          background: `radial-gradient(600px circle at ${mousePos.x}px ${mousePos.y}px, rgba(99,102,241,0.06), transparent 60%)`,
        }}
      />

      {/* Nav */}
      <nav className="relative z-10 flex items-center justify-between px-4 sm:px-6 py-4 sm:py-5 max-w-7xl mx-auto border-b border-slate-200 bg-white/80 backdrop-blur-sm">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <img
            src="/assets/images/LOGO_1_-1775142741753.png"
            alt="Crack AIBE - Commercial Law Publishers"
            className="h-7 w-auto sm:h-12 object-contain flex-shrink-0"
          />
          <span className="text-slate-900 font-bold text-base sm:text-xl tracking-tight whitespace-nowrap">Crack AIBE</span>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
          <Link
            href="/login"
            className="px-3 sm:px-5 py-2 text-xs sm:text-sm font-semibold text-indigo-600 hover:text-indigo-800 transition-colors duration-200"
          >
            Sign In
          </Link>
          <Link
            href="/signup"
            className="px-3 sm:px-5 py-2 text-xs sm:text-sm font-semibold bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-all duration-200 shadow-md shadow-indigo-200"
          >
            Get Started
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 pt-16 pb-24">
        {/* Ambient glow blobs */}
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-indigo-100/80 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute top-20 right-1/4 w-[400px] h-[400px] bg-violet-100/60 rounded-full blur-[100px] pointer-events-none" />

        <div className="relative text-center max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-full px-4 py-1.5 text-xs font-medium text-indigo-600 mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            India&apos;s most comprehensive AIBE prep platform
          </div>

          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight mb-6">
            <span className="text-slate-900">Pass AIBE.</span>
            <br />
            <span className="bg-gradient-to-r from-indigo-600 via-violet-600 to-indigo-500 bg-clip-text text-transparent">
              On your first attempt.
            </span>
          </h1>

          <p className="text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto mb-10">
            Over <strong className="text-slate-900">10,000 practice questions</strong> mapped to every AIBE topic. 
            Full-length mock tests, instant explanations, and analytics that show exactly where you stand.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              href="/login"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-white hover:bg-slate-100 border border-slate-200 text-slate-800 font-semibold rounded-xl transition-all duration-200 text-base shadow-sm"
            >
              Sign In
            </Link>
          </div>
        </div>

        {/* Hero visual — mock UI card */}
        <div className="relative mt-20 max-w-3xl mx-auto">
          <div className="absolute inset-0 bg-gradient-to-b from-indigo-200/40 to-violet-200/20 rounded-2xl blur-2xl scale-105" />
          <div className="relative bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-xl">
            {/* Mock header bar */}
            <div className="flex items-center gap-2 px-5 py-3.5 border-b border-slate-100 bg-slate-50">
              <div className="w-3 h-3 rounded-full bg-red-400" />
              <div className="w-3 h-3 rounded-full bg-yellow-400" />
              <div className="w-3 h-3 rounded-full bg-green-400" />
              <span className="ml-3 text-xs text-slate-400 font-mono">crackaibe.com — Practice Test</span>
            </div>
            {/* Mock question UI */}
            <div className="p-6 sm:p-8">
              <div className="flex items-center justify-between mb-5">
                <span className="text-xs font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 px-3 py-1 rounded-full">Constitutional Law · Q 14 of 100</span>
                <span className="text-xs text-slate-400 font-mono">02:34 remaining</span>
              </div>
              <p className="text-sm sm:text-base text-slate-700 leading-relaxed mb-6">
                Under Article 21 of the Constitution of India, the right to life and personal liberty can be restricted by procedure established by law. Which landmark case expanded this to include &quot;due process&quot;?
              </p>
              <div className="space-y-2.5">
                {[
                  { label: 'A', text: 'A.K. Gopalan v. State of Madras', correct: false },
                  { label: 'B', text: 'Maneka Gandhi v. Union of India', correct: true },
                  { label: 'C', text: 'Kesavananda Bharati v. State of Kerala', correct: false },
                  { label: 'D', text: 'Minerva Mills v. Union of India', correct: false },
                ].map((opt) => (
                  <div
                    key={opt.label}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg border text-sm transition-all ${
                      opt.correct
                        ? 'bg-emerald-50 border-emerald-300 text-emerald-700' :'bg-slate-50 border-slate-200 text-slate-600'
                    }`}
                  >
                    <span className={`w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold flex-shrink-0 ${opt.correct ? 'bg-emerald-200 text-emerald-700' : 'bg-slate-200 text-slate-500'}`}>
                      {opt.label}
                    </span>
                    {opt.text}
                    {opt.correct && (
                      <svg className="w-4 h-4 ml-auto text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="relative z-10 border-y border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-14 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="text-3xl mb-2">{stat.icon}</div>
              <div className="text-3xl sm:text-4xl font-bold text-slate-900 mb-1">
                {stat.value.includes('+') ? (
                  <>
                    <AnimatedCounter
                      target={parseInt(stat.value.replace(/[^0-9]/g, ''))}
                      suffix=""
                    />
                    +
                  </>
                ) : stat.value.includes('%') ? (
                  <>
                    <AnimatedCounter target={parseInt(stat.value)} />%
                  </>
                ) : (
                  <>
                    <AnimatedCounter target={parseInt(stat.value.replace(/[^0-9]/g, ''))} />+
                  </>
                )}
              </div>
              <div className="text-sm text-slate-500">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Features — Bento Grid */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 py-24">
        <div className="text-center mb-14">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            Everything you need to clear AIBE
          </h2>
          <p className="text-slate-500 max-w-xl mx-auto">
            Built specifically for law graduates preparing for the Bar Council exam — not a generic quiz app.
          </p>
        </div>

        {/* Bento grid — asymmetric layout */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Large card — spans 2 cols on lg */}
          <div className="lg:col-span-2 bg-gradient-to-br from-indigo-50 to-white border border-indigo-200 rounded-2xl p-8 flex flex-col justify-between min-h-[220px] hover:border-indigo-400 hover:shadow-md transition-all duration-300 group">
            <div className="text-4xl mb-4">{features[0].icon}</div>
            <div>
              <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-indigo-600 transition-colors">{features[0].title}</h3>
              <p className="text-slate-500 text-sm leading-relaxed">{features[0].description}</p>
            </div>
          </div>

          {/* Small card */}
          <div className="bg-white border border-slate-200 rounded-2xl p-7 flex flex-col justify-between min-h-[220px] hover:border-indigo-300 hover:shadow-md transition-all duration-300 group">
            <div className="text-3xl mb-4">{features[1].icon}</div>
            <div>
              <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-indigo-600 transition-colors">{features[1].title}</h3>
              <p className="text-slate-500 text-sm leading-relaxed">{features[1].description}</p>
            </div>
          </div>

          {/* Small card */}
          <div className="bg-white border border-slate-200 rounded-2xl p-7 flex flex-col justify-between min-h-[200px] hover:border-violet-300 hover:shadow-md transition-all duration-300 group">
            <div className="text-3xl mb-4">{features[2].icon}</div>
            <div>
              <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-violet-600 transition-colors">{features[2].title}</h3>
              <p className="text-slate-500 text-sm leading-relaxed">{features[2].description}</p>
            </div>
          </div>

          {/* Medium card — spans 2 cols on lg */}
          <div className="lg:col-span-2 bg-gradient-to-br from-violet-50 to-white border border-violet-200 rounded-2xl p-7 flex flex-col justify-between min-h-[200px] hover:border-violet-400 hover:shadow-md transition-all duration-300 group">
            <div className="text-3xl mb-4">{features[3].icon}</div>
            <div>
              <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-violet-600 transition-colors">{features[3].title}</h3>
              <p className="text-slate-500 text-sm leading-relaxed">{features[3].description}</p>
            </div>
          </div>

          {/* Last card — full width on sm, 1 col on lg */}
          <div className="sm:col-span-2 lg:col-span-1 bg-gradient-to-br from-amber-50 to-white border border-amber-200 rounded-2xl p-7 flex flex-col justify-between min-h-[200px] hover:border-amber-400 hover:shadow-md transition-all duration-300 group">
            <div className="text-3xl mb-4">{features[4].icon}</div>
            <div>
              <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-amber-600 transition-colors">{features[4].title}</h3>
              <p className="text-slate-500 text-sm leading-relaxed">{features[4].description}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Topics strip */}
      <section className="relative z-10 border-y border-slate-200 bg-white overflow-hidden py-10">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-center text-xs font-semibold text-slate-400 uppercase tracking-widest mb-6">Topics covered</p>
          <div className="flex flex-wrap justify-center gap-2.5">
            {topics.map((topic) => (
              <span
                key={topic}
                className="px-3 py-1.5 text-xs font-medium text-slate-600 bg-slate-100 border border-slate-200 rounded-full hover:text-indigo-600 hover:border-indigo-300 hover:bg-indigo-50 transition-colors cursor-default"
              >
                {topic}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative z-10 max-w-4xl mx-auto px-6 py-24">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Frequently Asked Questions</h2>
          <p className="text-slate-500 max-w-xl mx-auto">
            Everything you need to know about AIBE prep, subscriptions, and how Crack AIBE works.
          </p>
        </div>
        <div className="space-y-3">
          {faqs.map((faq) => (
            <FAQItem key={faq.question} question={faq.question} answer={faq.answer} />
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 py-28 text-center">
        <div className="relative max-w-2xl mx-auto">
          <div className="absolute inset-0 bg-indigo-100 rounded-3xl blur-3xl scale-110 pointer-events-none" />
          <div className="relative bg-gradient-to-br from-indigo-50 to-white border border-indigo-200 rounded-3xl px-8 py-14 shadow-lg">
            <div className="text-5xl mb-5">⚖️</div>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4 leading-tight">
              Your bar licence is one exam away.
            </h2>
            <p className="text-slate-500 mb-8 leading-relaxed">
              Join thousands of law graduates who used Crack AIBE to prepare smarter, not harder.
            </p>
            <Link
              href="/login"
              className="inline-flex items-center gap-2 px-10 py-4 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl transition-all duration-200 shadow-lg shadow-indigo-200 text-base group"
            >
              Login to Your Account
              <svg className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <p className="mt-4 text-xs text-slate-400">
              New here?{' '}
              <Link href="/signup" className="text-indigo-600 hover:text-indigo-800 transition-colors">
                Create a free account
              </Link>
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-slate-200 bg-white py-8 px-6">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-400">
          <div className="flex items-center gap-2">
            <img src="/assets/images/Logo-1774427039733.png" alt="Crack AIBE" className="h-5 w-auto opacity-60" />
            <span>© 2026 Crack AIBE. All rights reserved.</span>
          </div>
          <div className="flex items-center gap-5">
            <Link href="/privacy-policy" className="hover:text-slate-600 transition-colors">Privacy Policy</Link>
            <Link href="/terms-and-conditions" className="hover:text-slate-600 transition-colors">Terms</Link>
            <Link href="/refund-policy" className="hover:text-slate-600 transition-colors">Refund Policy</Link>
            <Link href="/login" className="hover:text-slate-600 transition-colors">Login</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
